import type { StatusDefinitionRow, StatusColorGroup } from '../statusDefinitions';
import type { StatusTransitionRow } from '../permissions/guards';
import { PACKAGE_STATUSES } from './package';
import { isPastDue } from '../reports/aging';

/**
 * The status vocabulary of the accounting documents (piece 22): one
 * StatusDefinition row per member of every status enum the piece declares,
 * keyed on the enum a field is typed as, and the five transition tables
 * canTransition reads as StatusTransition rows. The migration seeds the same
 * rows from this module, and the database suite holds the table to it.
 *
 * 🔨 A derived member is never a from-state or a to-state: Expired on the
 * Quote and the paid states on the Invoice, the Bill, the Credit Note and the
 * Vendor Credit shadow the stored value at read time, and each column refuses
 * them. They still take a row, because the pill renders them.
 */
export const APPROVAL_STATUSES = ['NotRequired', 'Pending', 'Approved', 'Rejected'] as const;
export type ApprovalStatusValue = (typeof APPROVAL_STATUSES)[number];

/** Sending and DeliveryFailed joined last (decision 1225), so every earlier row keeps its sort order; both are derived. */
export const QUOTE_STATUSES = ['Draft', 'Sent', 'ClientViewed', 'Accepted', 'Declined', 'Void', 'Expired', 'Sending', 'DeliveryFailed'] as const;
export type QuoteStatusValue = (typeof QUOTE_STATUSES)[number];
export const SALES_ORDER_STATUSES = ['Draft', 'Sent', 'ClientViewed', 'PendingCustomerPO', 'Released', 'Closed', 'Void', 'Sending', 'DeliveryFailed'] as const;
export type SalesOrderStatusValue = (typeof SALES_ORDER_STATUSES)[number];
export const FULFILLMENT_STATUSES = ['NotStarted', 'InProgress', 'Fulfilled', 'Cancelled'] as const;
export type FulfillmentStatusValue = (typeof FULFILLMENT_STATUSES)[number];
export const BRANCH_STATUSES = ['NotStarted', 'InProgress', 'Complete', 'NotApplicable'] as const;
export type BranchStatusValue = (typeof BRANCH_STATUSES)[number];
/** PartlyWrittenOff joined last (decision 1074), so every earlier row keeps its sort order. */
export const INVOICE_STATUSES = ['Draft', 'Sent', 'PartiallyPaid', 'Overdue', 'Disputed', 'Paid', 'WrittenOff', 'Void', 'PartlyWrittenOff'] as const;
export type InvoiceStatusValue = (typeof INVOICE_STATUSES)[number];
export const PURCHASE_ORDER_FULFILLMENT_STATUSES = ['NotStarted', 'PartiallyReceived', 'Received', 'Cancelled'] as const;
/** Decision 1072: the Purchase Order's one linear status, in the order its gates move it. */
export const PURCHASE_ORDER_STATUSES = ['Draft', 'Approved', 'Sent', 'PartlyReceived', 'Received', 'Closed', 'Cancelled', 'Sending', 'DeliveryFailed'] as const;
export type PurchaseOrderStatusValue = (typeof PURCHASE_ORDER_STATUSES)[number];
export const WORK_ORDER_STATUSES = ['Draft', 'Scheduled', 'Dispatched', 'InProgress', 'Complete', 'Closed', 'Void'] as const;
export type WorkOrderStatusValue = (typeof WORK_ORDER_STATUSES)[number];
export const LINE_COMPLETION_STATUSES = ['Pending', 'Complete', 'NotComplete', 'NotNeeded'] as const;
export const BILL_STATUSES = ['Draft', 'Received', 'PartiallyPaid', 'Paid', 'Void'] as const;
export const CREDIT_NOTE_STATUSES = ['Draft', 'PendingReturn', 'Issued', 'Applied', 'Void'] as const;
export type CreditNoteStatusValue = (typeof CREDIT_NOTE_STATUSES)[number];
export const CHANGE_ORDER_STATUSES = ['Draft', 'PendingApproval', 'Approved', 'Rejected', 'Withdrawn'] as const;
export type ChangeOrderStatusValue = (typeof CHANGE_ORDER_STATUSES)[number];
export const VENDOR_CREDIT_STATUSES = ['Draft', 'Open', 'PartiallyApplied', 'Applied', 'Void'] as const;
export const PAYMENT_STATUSES = ['Draft', 'Recorded', 'Cleared', 'Void'] as const;
export type PaymentStatusValue = (typeof PAYMENT_STATUSES)[number];
export const REFUND_STATUSES = ['Pending', 'Sent', 'Failed', 'Void'] as const;
export type RefundStatusValue = (typeof REFUND_STATUSES)[number];
export const DEPOSIT_STATUSES = ['Open', 'Deposited', 'Void'] as const;
export type DepositStatusValue = (typeof DEPOSIT_STATUSES)[number];
export const SHIPMENT_STATUSES = ['Draft', 'Packed', 'Shipped', 'Delivered', 'Cancelled'] as const;
export const STOCK_TRANSFER_STATUSES = ['Draft', 'InTransit', 'Received', 'Cancelled'] as const;
export const STOCK_COUNT_STATUSES = ['Draft', 'Counting', 'Review', 'Posted', 'Cancelled'] as const;

/** The members each column refuses, because they derive at read time (piece 22, the derived-member rule). */
export const DERIVED_STATUS_MEMBERS: Readonly<Record<string, readonly string[]>> = {
  QuoteStatus: ['Expired', 'Sending', 'DeliveryFailed'],
  SalesOrderStatus: ['Sending', 'DeliveryFailed'],
  PurchaseOrderStatus: ['Sending', 'DeliveryFailed'],
  InvoiceStatus: ['PartiallyPaid', 'Overdue', 'Paid', 'PartlyWrittenOff'],
  BillStatus: ['PartiallyPaid', 'Paid'],
  CreditNoteStatus: ['Applied'],
  VendorCreditStatus: ['PartiallyApplied', 'Applied'],
};

interface Vocabulary {
  entityType: string;
  members: readonly string[];
  /** The colour group per member; a member absent here is good. */
  groups: Readonly<Record<string, StatusColorGroup>>;
  labels?: Readonly<Record<string, string>>;
}

/** A settled draft is inactive, a wait is a warning, a refusal or a state somebody must act on is an error, the rest good. */
const VOCABULARIES: readonly Vocabulary[] = [
  { entityType: 'ApprovalStatus', members: APPROVAL_STATUSES, groups: { NotRequired: 'inactive', Pending: 'warning', Rejected: 'error' }, labels: { NotRequired: 'Not Required' } },
  { entityType: 'QuoteStatus', members: QUOTE_STATUSES, groups: { Draft: 'inactive', Declined: 'error', Void: 'inactive', Expired: 'warning', Sending: 'warning', DeliveryFailed: 'error' }, labels: { ClientViewed: 'Client Viewed', DeliveryFailed: 'Delivery Failed' } },
  {
    entityType: 'SalesOrderStatus', members: SALES_ORDER_STATUSES, groups: { Draft: 'inactive', PendingCustomerPO: 'warning', Closed: 'inactive', Void: 'inactive', Sending: 'warning', DeliveryFailed: 'error' },
    labels: { ClientViewed: 'Client Viewed', PendingCustomerPO: 'Pending Customer PO', DeliveryFailed: 'Delivery Failed' },
  },
  { entityType: 'FulfillmentStatus', members: FULFILLMENT_STATUSES, groups: { NotStarted: 'inactive', InProgress: 'warning', Cancelled: 'inactive' }, labels: { NotStarted: 'Not Started', InProgress: 'In Progress', Cancelled: 'Canceled' } },
  { entityType: 'BranchStatus', members: BRANCH_STATUSES, groups: { NotStarted: 'inactive', InProgress: 'warning', NotApplicable: 'inactive' }, labels: { NotStarted: 'Not Started', InProgress: 'In Progress', NotApplicable: 'N/A' } },
  {
    entityType: 'InvoiceStatus', members: INVOICE_STATUSES, groups: { Draft: 'inactive', PartiallyPaid: 'warning', Overdue: 'error', Disputed: 'error', WrittenOff: 'inactive', Void: 'inactive', PartlyWrittenOff: 'inactive' },
    labels: { PartiallyPaid: 'Partially Paid', WrittenOff: 'Written Off', PartlyWrittenOff: 'Paid, Partly Written Off' },
  },
  {
    entityType: 'PurchaseOrderFulfillmentStatus', members: PURCHASE_ORDER_FULFILLMENT_STATUSES, groups: { NotStarted: 'inactive', PartiallyReceived: 'warning', Cancelled: 'inactive' },
    labels: { NotStarted: 'Not Started', PartiallyReceived: 'Partially Received', Cancelled: 'Canceled' },
  },
  {
    entityType: 'PurchaseOrderStatus', members: PURCHASE_ORDER_STATUSES, groups: { Draft: 'inactive', Approved: 'good', Sent: 'good', PartlyReceived: 'warning', Closed: 'inactive', Cancelled: 'inactive', Sending: 'warning', DeliveryFailed: 'error' },
    labels: { PartlyReceived: 'Partly Received', DeliveryFailed: 'Delivery Failed', Cancelled: 'Canceled' },
  },
  { entityType: 'WorkOrderStatus', members: WORK_ORDER_STATUSES, groups: { Draft: 'inactive', Closed: 'inactive', Void: 'inactive' }, labels: { InProgress: 'In Progress' } },
  { entityType: 'LineCompletionStatus', members: LINE_COMPLETION_STATUSES, groups: { Pending: 'warning', NotComplete: 'error', NotNeeded: 'inactive' }, labels: { NotComplete: 'Not Complete', NotNeeded: 'Not Needed' } },
  { entityType: 'BillStatus', members: BILL_STATUSES, groups: { Draft: 'inactive', PartiallyPaid: 'warning', Void: 'inactive' }, labels: { PartiallyPaid: 'Partially Paid' } },
  { entityType: 'CreditNoteStatus', members: CREDIT_NOTE_STATUSES, groups: { Draft: 'inactive', PendingReturn: 'warning', Void: 'inactive' }, labels: { PendingReturn: 'Pending Return' } },
  { entityType: 'ChangeOrderStatus', members: CHANGE_ORDER_STATUSES, groups: { Draft: 'inactive', PendingApproval: 'warning', Rejected: 'error', Withdrawn: 'inactive' }, labels: { PendingApproval: 'Pending Approval' } },
  { entityType: 'VendorCreditStatus', members: VENDOR_CREDIT_STATUSES, groups: { Draft: 'inactive', PartiallyApplied: 'warning', Void: 'inactive' }, labels: { PartiallyApplied: 'Partially Applied' } },
  { entityType: 'PaymentStatus', members: PAYMENT_STATUSES, groups: { Draft: 'inactive', Void: 'inactive' } },
  { entityType: 'RefundStatus', members: REFUND_STATUSES, groups: { Pending: 'warning', Failed: 'error', Void: 'inactive' } },
  { entityType: 'DepositStatus', members: DEPOSIT_STATUSES, groups: { Open: 'warning', Void: 'inactive' } },
  { entityType: 'ShipmentStatus', members: SHIPMENT_STATUSES, groups: { Draft: 'inactive', Cancelled: 'inactive' }, labels: { Cancelled: 'Canceled' } },
  // Decision 1730: a package waits grey until a shipment carries it, as the Zoho sheet's band draws it.
  { entityType: 'PackageStatus', members: PACKAGE_STATUSES, groups: { NotShipped: 'inactive' }, labels: { NotShipped: 'Not Shipped' } },
  { entityType: 'StockTransferStatus', members: STOCK_TRANSFER_STATUSES, groups: { Draft: 'inactive', InTransit: 'warning', Cancelled: 'inactive' }, labels: { InTransit: 'In Transit', Cancelled: 'Canceled' } },
  { entityType: 'StockCountStatus', members: STOCK_COUNT_STATUSES, groups: { Draft: 'inactive', Counting: 'warning', Review: 'warning', Cancelled: 'inactive' }, labels: { Review: 'In Review', Cancelled: 'Canceled' } },
];

/** The enums this piece seeds rows for; TravelSource, the payment and refund methods, displayMode and roundingMode are values, never pills. */
export const DOCUMENT_STATUS_ENUMS: readonly string[] = VOCABULARIES.map((v) => v.entityType);

export const DOCUMENT_STATUS_DEFINITIONS: readonly StatusDefinitionRow[] = VOCABULARIES.flatMap((v) =>
  v.members.map((value, i): StatusDefinitionRow => ({
    entityType: v.entityType,
    statusValue: value,
    label: v.labels?.[value] ?? value,
    colorGroup: v.groups[value] ?? 'good',
    sortOrder: i + 1,
  })),
);

/**
 * 🔨 A document page's status pill reads its label and its colour off this one
 * vocabulary, never off a map on the screen. A member with no row here prints
 * its own stored spelling rather than inventing a label.
 *
 * 🔨 The index is built here rather than through `createStatusResolver`, because
 * `statusDefinitions.ts` reads this module's rows: importing its function back
 * would close the cycle and leave the seed undefined at load (found while
 * building, 2026-09-15).
 */
const DOCUMENT_STATUS_INDEX = new Map(DOCUMENT_STATUS_DEFINITIONS.map((row) => [`${row.entityType} ${row.statusValue}`, row]));

export function documentStatusPill(entityType: string, statusValue: string): { text: string; group: StatusColorGroup } {
  const row = DOCUMENT_STATUS_INDEX.get(`${entityType} ${statusValue}`);
  return row ? { text: row.label, group: row.colorGroup } : { text: statusValue, group: 'inactive' };
}

export interface DocumentTransition {
  entityType: string;
  from: string;
  to: string;
}

const table = (entityType: string, rows: Readonly<Record<string, readonly string[]>>): DocumentTransition[] =>
  Object.entries(rows).flatMap(([from, tos]) => tos.map((to) => ({ entityType, from, to })));

/**
 * The five transition tables of piece 22, over stored members only. A blank
 * To cell is a terminal state, so Void, Closed, Fulfilled and Cancelled name
 * no row of their own.
 */
export const DOCUMENT_STATUS_TRANSITIONS: readonly DocumentTransition[] = [
  ...table('QuoteStatus', {
    Draft: ['Sent', 'Void'],
    Sent: ['ClientViewed', 'Accepted', 'Declined', 'Draft', 'Void'],
    ClientViewed: ['Accepted', 'Declined', 'Draft', 'Void'],
    Accepted: ['Void'],
    Declined: ['Draft', 'Void'],
  }),
  ...table('SalesOrderStatus', {
    Draft: ['Sent', 'PendingCustomerPO', 'Void'],
    Sent: ['ClientViewed', 'PendingCustomerPO', 'Released', 'Draft', 'Void'],
    ClientViewed: ['PendingCustomerPO', 'Released', 'Draft', 'Void'],
    PendingCustomerPO: ['Released', 'Draft', 'Void'],
    Released: ['Closed', 'Void'],
  }),
  ...table('FulfillmentStatus', {
    NotStarted: ['InProgress', 'Cancelled'],
    InProgress: ['Fulfilled', 'Cancelled'],
  }),
  ...table('InvoiceStatus', {
    Draft: ['Sent', 'Void'],
    Sent: ['Disputed', 'WrittenOff', 'Void'],
    Disputed: ['Sent', 'WrittenOff'],
    WrittenOff: ['Sent'],
  }),
  ...table('WorkOrderStatus', {
    Draft: ['Scheduled', 'Void'],
    Scheduled: ['Dispatched', 'Draft', 'Void'],
    // 🔒 Decision 4340: a Dispatched job goes back by its visit, never by a move of its own; the row back to Scheduled is retired below.
    Dispatched: ['InProgress', 'Void'],
    InProgress: ['Complete', 'Void'],
    Complete: ['Closed'],
  }),
];

/**
 * 🔒 Decision 4340: the rows a decision retired. The record-layer migrations
 * wrote them, so the seed sets each one inactive, which `canTransition` reads,
 * and the row stands for the history of the moves made by it.
 */
export const RETIRED_STATUS_TRANSITIONS: readonly { entityType: string; from: string; to: string }[] = [{ entityType: 'WorkOrderStatus', from: 'Dispatched', to: 'Scheduled' }];

/** The five tables as the rows canTransition reads, for a caller holding no database. */
export const DOCUMENT_TRANSITION_ROWS: readonly StatusTransitionRow[] = DOCUMENT_STATUS_TRANSITIONS.map((t) => ({
  entityType: t.entityType,
  fromStatus: t.from,
  toStatus: t.to,
  isActive: true,
}));

/** The enums whose transitions this piece constrains; an enum with no rows accepts any move among its stored members. */
export const CONSTRAINED_STATUS_ENUMS = ['QuoteStatus', 'SalesOrderStatus', 'FulfillmentStatus', 'InvoiceStatus', 'WorkOrderStatus'] as const;

/** Whether the enum's own table allows the move; a table never names a derived member, so a derived from or to is refused here too. */
export function documentTransitionAllowed(entityType: string, from: string, to: string): boolean {
  if (!(CONSTRAINED_STATUS_ENUMS as readonly string[]).includes(entityType)) return true;
  return DOCUMENT_STATUS_TRANSITIONS.some((t) => t.entityType === entityType && t.from === from && t.to === to);
}

/** 🔨 The gate has cleared a document at Approved and at NotRequired, and at neither of the other two (decision 0411). */
export function approvalCleared(approvalStatus: ApprovalStatusValue): boolean {
  return approvalStatus === 'Approved' || approvalStatus === 'NotRequired';
}

/**
 * Decision 0093 as a pure rule, amended by decision 0411: fulfillmentStatus
 * cannot leave NotStarted until the gate has cleared the order. Pending and
 * Rejected refuse the move, which is 0093's guarantee; NotRequired clears it,
 * because a document inside the submitter's rails awaits nobody's signature.
 * The write path asks this beside the transition table, and the database
 * trigger asks it again.
 */
export function fulfillmentMoveAllowed(from: FulfillmentStatusValue, to: FulfillmentStatusValue, approvalStatus: ApprovalStatusValue): boolean {
  if (!documentTransitionAllowed('FulfillmentStatus', from, to)) return false;
  if (from === 'NotStarted' && to !== 'NotStarted' && !approvalCleared(approvalStatus)) return false;
  return true;
}

/**
 * The join of the two branches (piece 22): Fulfilled when both are Complete
 * or NotApplicable, InProgress when either has started (In progress or
 * Complete), else NotStarted. A branch marked N/A starts nothing on its own
 * (T913, decision 1711), so an order converted with one branch N/A opens Not
 * started. Cancelled is a person's write and never recomputed.
 */
export function fulfillmentFromBranches(parts: BranchStatusValue, labour: BranchStatusValue): Exclude<FulfillmentStatusValue, 'Cancelled'> {
  const done = (b: BranchStatusValue) => b === 'Complete' || b === 'NotApplicable';
  const started = (b: BranchStatusValue) => b === 'InProgress' || b === 'Complete';
  if (done(parts) && done(labour)) return 'Fulfilled';
  if (started(parts) || started(labour)) return 'InProgress';
  return 'NotStarted';
}

/**
 * The read-time shadow of a derived member (piece 22): a Sent Quote past its
 * expiry reads Expired; an Invoice reads Paid, PartiallyPaid or Overdue from
 * its allocation sum and its due date.
 *
 * 🔒 Paid shadows every stored member but Draft, Void and WrittenOff (T132,
 * T431; decisions 0710, 1074): an invoice whose allocations cover its total is
 * paid whether a person disputed it first. A write-off is read off the stored
 * WrittenOff, the write-off's own row, and never off the balance: it reads
 * Written Off, or Paid, Partly Written Off where money was also allocated to it.
 * A payment after the write-off reverses it first (decision 0197), so the
 * stored member is Sent again and the status follows. PartiallyPaid and
 * Overdue shadow Sent alone, because Disputed and Written Off are a person's
 * own statement about the balance and say more than either.
 */
export function displayedQuoteStatus(stored: QuoteStatusValue, expiryDate: Date, now: Date): QuoteStatusValue {
  return stored === 'Sent' && expiryDate.getTime() < now.getTime() ? 'Expired' : stored;
}

/**
 * 🔒 The pill a Draft the approval gate holds reads (T1195; decision 1861): Pending Approval while
 * its approval is Pending and Rejected once rejected, in the colours the approval pill draws. The
 * stored status stays Draft and no status member is added; null where the approval adds nothing,
 * so the caller draws the displayed status as before.
 */
export function heldDraftPill(displayed: string, approvalStatus: string | null | undefined): { text: string; group: StatusColorGroup } | null {
  if (displayed !== 'Draft') return null;
  if (approvalStatus === 'Pending') return { text: 'Pending Approval', group: documentStatusPill('ApprovalStatus', 'Pending').group };
  if (approvalStatus === 'Rejected') return { text: documentStatusPill('ApprovalStatus', 'Rejected').text, group: documentStatusPill('ApprovalStatus', 'Rejected').group };
  return null;
}

/**
 * 🔒 An Invoice's displayed status. `today` is the calendar day in the
 * organization's zone, which the caller reads through the organization-today
 * helper; Overdue is the due day before it (decision 2831), the aging's own rule.
 * A Sent invoice whose total is zero reads Paid (decision 3264, amended).
 */
export function displayedInvoiceStatus(stored: InvoiceStatusValue, facts: { totalCents: number; amountPaidCents: number; dueDate: Date; today: string }): InvoiceStatusValue {
  if (stored === 'Draft' || stored === 'Void') return stored;
  if (stored === 'WrittenOff') return facts.amountPaidCents > 0 ? 'PartlyWrittenOff' : 'WrittenOff';
  // 🔒 Decision 3264, amended 2026-10-27: an invoice totalling zero owes nothing, so it reads Paid before Overdue can be asked.
  if ((stored === 'Sent' && facts.totalCents === 0) || (facts.totalCents > 0 && facts.amountPaidCents >= facts.totalCents)) return 'Paid';
  if (stored !== 'Sent') return stored;
  if (isPastDue(facts.dueDate.toISOString().slice(0, 10), facts.today)) return 'Overdue';
  if (facts.amountPaidCents > 0) return 'PartiallyPaid';
  return stored;
}

/**
 * 🔒 The Credit Note's displayed status (piece 22; T481): Applied derives from
 * the open allocation sum over a stored Issued, and every other member is the
 * column's own. A note whose allocations cover its total reads Applied; one
 * allocated in part, because its invoice owed less, still reads Issued.
 */
export function displayedCreditNoteStatus(stored: CreditNoteStatusValue, facts: { totalCents: number; appliedCents: number }): CreditNoteStatusValue {
  if (stored !== 'Issued') return stored;
  return facts.totalCents > 0 && facts.appliedCents >= facts.totalCents ? 'Applied' : stored;
}

/**
 * 🔒 Whether a Credit Note has credited its invoice, read off the stored column
 * (T481). Applied is derived and never stored, so the one stored member that
 * has credited is Issued; a test for a stored Applied matches nothing.
 */
export function creditNoteHasCredited(stored: string): boolean {
  return stored === 'Issued';
}

/**
 * 🔒 The stored members a query may narrow on for a set of displayed Invoice
 * statuses (T132; decision 0710). A derived member is never in the column, so
 * a `where` naming one matches nothing; it stands for the stored members it can
 * shadow, and the caller filters the resolved answer afterwards.
 */
export function storedInvoiceStatusesFor(displayed: readonly string[]): InvoiceStatusValue[] {
  const out = new Set<InvoiceStatusValue>();
  for (const member of displayed) {
    // 🔨 Since decision 1074 a stored WrittenOff never reads Paid; it stays here only because the caller filters the resolved answer, so a caller narrowing on bare Paid must filter too.
    if (member === 'Paid') ['Sent', 'Disputed', 'WrittenOff'].forEach((m) => out.add(m as InvoiceStatusValue));
    else if (member === 'PartiallyPaid' || member === 'Overdue') out.add('Sent');
    else if (member === 'PartlyWrittenOff') out.add('WrittenOff');
    else if ((INVOICE_STATUSES as readonly string[]).includes(member)) out.add(member as InvoiceStatusValue);
  }
  return [...out];
}
